import React, { type ChangeEvent, type ReactNode } from 'react';

import classnames from 'classnames';

import { Icon, Spinner, Text, Tooltip, type TooltipProps, useSize } from 'styled-system';

import { TOOLTIP } from '../../../dataTestIds';
import { type SizeType } from '../../features/config/SizeContext';
import useControlledState from '../../hooks/useControlledState';
import { type InputBaseProps } from '../../types/formControls';
import { devWarn, Key, resolveOnChangeEvent } from '../../utils';

import styles from './Switch.module.scss';

export type SwitchProps = InputBaseProps<{
    /**
     * The id of the switch label.
     */
    id?: string;
    /**
     * The content of the switch label.
     */
    children?: ReactNode;
    /**
     * Class name to apply to the switch input.
     */
    className?: string;
    /**
     * If the switch is disabled.
     */
    disabled?: boolean;
    /**
     * Whether the switch is checked.
     */
    checked?: boolean;
    /**
     * Specifies the initial state: whether the switch is checked.
     */
    defaultChecked?: boolean;
    /**
     * The size of the switch.
     */
    size?: SizeType;
    /**
     * Whether the switch should show a loading indicator.
     */
    loading?: boolean;
    /**
     * An informatory tooltip that is displayed when hovering over the label icon.
     */
    tooltip?: string;
    tooltipId?: string;
    tooltipPlacement?: TooltipProps['placement'];
}>;

export type SwitchChangeEvent = ChangeEvent<HTMLInputElement>;

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>((props: SwitchProps, ref) => {
    const {
        checked: controlledValue,
        defaultChecked,
        disabled,
        id,
        onChange,
        size: sizeProp,
        children,
        loading,
        tooltip,
        tooltipId = '',
        tooltipPlacement = 'top',
        ...restProps
    } = props;
    const size = useSize(sizeProp, 'small', 'Switch');

    const [isChecked, setIsChecked] = useControlledState(
        controlledValue,
        defaultChecked,
        'Switch',
        'checked'
    );

    function triggerChange(
        newChecked: boolean,
        e: ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) {
        if (disabled) {
            return;
        }
        const changedEvent = resolveOnChangeEvent(
            e.target as HTMLInputElement,
            e as ChangeEvent<HTMLInputElement>
        );
        setIsChecked(newChecked);
        onChange?.(changedEvent as SwitchChangeEvent);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === Key.ArrowLeft) {
            triggerChange(false, e);
        }
        if (e.key === Key.ArrowRight) {
            triggerChange(true, e);
        }
    }

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        triggerChange(e.target.checked, e);
    }

    if (process.env.NODE_ENV !== 'production' && !children && !props?.['aria-label']) {
        devWarn('Provide an aria-label for accessibility.');
    }
    const LabelElement = children ? 'label' : 'div';

    return (
        <LabelElement
            className={classnames(styles.container, styles[size], {
                [styles.checked]: isChecked,
                [styles.disabled]: disabled
            })}
            htmlFor={id}
        >
            <span className={styles.switch}>
                <input
                    ref={ref}
                    type="checkbox"
                    role="switch"
                    id={id}
                    checked={!!isChecked}
                    aria-checked={!!isChecked}
                    disabled={disabled}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    {...restProps}
                />
                <div className={styles.handle}>
                    {loading && <Spinner color="primary" size="100%" />}
                </div>
            </span>
            {children && (
                <div className={styles.labelContainer}>
                    <Text className={styles.label}>{children}</Text>
                </div>
            )}
            {tooltip && (
                <Tooltip
                    id={tooltipId}
                    trigger={['hover', 'focus']}
                    placement={tooltipPlacement}
                    title={tooltip}
                >
                    <Icon
                        data-testid={TOOLTIP(tooltipId)}
                        className={styles.icon}
                        name="s-icon-circle-info"
                        tabIndex={0}
                        size="medium"
                    />
                </Tooltip>
            )}
        </LabelElement>
    );
});

Switch.displayName = 'Switch';
